/**
 * Issuing jurisdictions for every plate-state selector we render.
 *
 * SHARED because a plate's state is now collected in two places — the checkout
 * plate step and the account's Vehicles card — and a driver must not be offered
 * a jurisdiction in one that the other cannot represent. Duplicating a 63-entry
 * list is how the two quietly drift apart.
 *
 * A plate is unique only WITHIN the jurisdiction that issued it — `ABC1234` in
 * Georgia and `ABC1234` in Texas are two different cars. Spot's grant schema
 * carries this as `lpState`; we have simply never asked a driver for it.
 *
 * WHY CANADIAN PROVINCES ARE HERE. The API's `normalizePlateState` accepts any
 * two letters rather than validating against the 50 USPS codes, precisely so a
 * province plate (ON, QC, BC) is representable — those are ordinary on US roads
 * and are exactly the cross-border case this feature exists to disambiguate. A
 * selector that offered only US states would force a Toronto plate to be
 * recorded as a lie, which is worse than recording nothing. Keep the two lists
 * in agreement: anything offered here must survive that normalizer.
 *
 * WHY THE OPERATING STATES ARE NOT HOISTED TO THE TOP. We run in GA, TX, FL and
 * CA today, and the tempting optimization is to float those four above a
 * divider. We don't, because the field is PREFILLED with the zone's state: a
 * driver who needs this list at all is by definition out-of-state, so the four
 * we'd be promoting are the four they are least likely to want. Plain
 * alphabetical, with the prefill already selected, is the shorter path for the
 * person actually using it.
 */

export interface PlateStateOption {
  code: string
  label: string
}

/** US states + DC, alphabetical by label. */
const US_STATES: PlateStateOption[] = [
  { code: 'AL', label: 'Alabama' },
  { code: 'AK', label: 'Alaska' },
  { code: 'AZ', label: 'Arizona' },
  { code: 'AR', label: 'Arkansas' },
  { code: 'CA', label: 'California' },
  { code: 'CO', label: 'Colorado' },
  { code: 'CT', label: 'Connecticut' },
  { code: 'DE', label: 'Delaware' },
  { code: 'DC', label: 'District of Columbia' },
  { code: 'FL', label: 'Florida' },
  { code: 'GA', label: 'Georgia' },
  { code: 'HI', label: 'Hawaii' },
  { code: 'ID', label: 'Idaho' },
  { code: 'IL', label: 'Illinois' },
  { code: 'IN', label: 'Indiana' },
  { code: 'IA', label: 'Iowa' },
  { code: 'KS', label: 'Kansas' },
  { code: 'KY', label: 'Kentucky' },
  { code: 'LA', label: 'Louisiana' },
  { code: 'ME', label: 'Maine' },
  { code: 'MD', label: 'Maryland' },
  { code: 'MA', label: 'Massachusetts' },
  { code: 'MI', label: 'Michigan' },
  { code: 'MN', label: 'Minnesota' },
  { code: 'MS', label: 'Mississippi' },
  { code: 'MO', label: 'Missouri' },
  { code: 'MT', label: 'Montana' },
  { code: 'NE', label: 'Nebraska' },
  { code: 'NV', label: 'Nevada' },
  { code: 'NH', label: 'New Hampshire' },
  { code: 'NJ', label: 'New Jersey' },
  { code: 'NM', label: 'New Mexico' },
  { code: 'NY', label: 'New York' },
  { code: 'NC', label: 'North Carolina' },
  { code: 'ND', label: 'North Dakota' },
  { code: 'OH', label: 'Ohio' },
  { code: 'OK', label: 'Oklahoma' },
  { code: 'OR', label: 'Oregon' },
  { code: 'PA', label: 'Pennsylvania' },
  { code: 'RI', label: 'Rhode Island' },
  { code: 'SC', label: 'South Carolina' },
  { code: 'SD', label: 'South Dakota' },
  { code: 'TN', label: 'Tennessee' },
  { code: 'TX', label: 'Texas' },
  { code: 'UT', label: 'Utah' },
  { code: 'VT', label: 'Vermont' },
  { code: 'VA', label: 'Virginia' },
  { code: 'WA', label: 'Washington' },
  { code: 'WV', label: 'West Virginia' },
  { code: 'WI', label: 'Wisconsin' },
  { code: 'WY', label: 'Wyoming' },
]

/** Canadian provinces + territories, alphabetical by label. */
const CA_PROVINCES: PlateStateOption[] = [
  { code: 'AB', label: 'Alberta' },
  { code: 'BC', label: 'British Columbia' },
  { code: 'MB', label: 'Manitoba' },
  { code: 'NB', label: 'New Brunswick' },
  { code: 'NL', label: 'Newfoundland and Labrador' },
  { code: 'NT', label: 'Northwest Territories' },
  { code: 'NS', label: 'Nova Scotia' },
  { code: 'NU', label: 'Nunavut' },
  { code: 'ON', label: 'Ontario' },
  { code: 'PE', label: 'Prince Edward Island' },
  { code: 'QC', label: 'Quebec' },
  { code: 'SK', label: 'Saskatchewan' },
  { code: 'YT', label: 'Yukon' },
]

/**
 * Rendered as two <optgroup>s so a province is findable without scrolling past
 * 51 states, and so "AB" reads as Alberta rather than as a missing US state.
 */
export const PLATE_STATE_GROUPS: { label: string; options: PlateStateOption[] }[] = [
  { label: 'United States', options: US_STATES },
  { label: 'Canada', options: CA_PROVINCES },
]

const KNOWN = new Set([...US_STATES, ...CA_PROVINCES].map((o) => o.code))

/**
 * Normalize a zone's `state` into a code this selector can actually show.
 *
 * Returns '' for anything unrecognized rather than a near-miss, which matters:
 * a `<select>` handed a value with no matching <option> silently renders its
 * FIRST option instead — so a zone with a typo'd state would display "Alabama"
 * as though the driver had picked it. Empty selects the explicit placeholder,
 * which is honest and forces a choice.
 */
export function toPlateStateCode(raw: string | null | undefined): string {
  const clean = (raw ?? '').trim().toUpperCase()
  return KNOWN.has(clean) ? clean : ''
}

/** Is this a jurisdiction code a selector can actually show? Same set as
 *  `toPlateStateCode`, expressed as a predicate for validation callers. */
export function isPlateStateCode(raw: string | null | undefined): boolean {
  return toPlateStateCode(raw) !== ''
}

/**
 * The select value for "State unknown" (owner ruling 2026-10-21): the client's
 * list has no state for this plate. A UI-only sentinel. It never goes on the
 * wire as a state: `plateStateWire` turns it into `{ stateUnknown: true }`, and
 * the API refuses the string "UNKNOWN" as a state. It is never a default; a
 * plate starts at the "Select a state" placeholder like any other.
 */
export const PLATE_STATE_UNKNOWN = 'UNKNOWN'
export const PLATE_STATE_UNKNOWN_LABEL = 'State unknown'

/** A select value from a stored vehicle: its state, the unknown sentinel, or ''. */
export function plateStateChoice(v: { state?: string | null; stateUnknown?: boolean | null } | null | undefined): string {
  const code = toPlateStateCode(v?.state)
  if (code) return code
  return v?.stateUnknown ? PLATE_STATE_UNKNOWN : ''
}

/** Has this plate been answered: a real state, or an explicit "State unknown"? */
export function isPlateStateChoice(raw: string | null | undefined): boolean {
  return raw === PLATE_STATE_UNKNOWN || isPlateStateCode(raw)
}

/** The fields a vehicle sends for a select value. A blank sends nothing, so the API still refuses it on a new plate. */
export function plateStateWire(choice: string | null | undefined): { state?: string; stateUnknown?: true } {
  if (choice === PLATE_STATE_UNKNOWN) return { stateUnknown: true }
  const code = toPlateStateCode(choice)
  return code ? { state: code } : {}
}
